import { useEffect, useState } from 'react';
import './CardsAndBanksTab.scss';
import trashIcon from '../../../../assets/icons/trashr.svg';
import DropDownArrowUp from '../../../../assets/icons/dropdown-arrow-up.svg';
import DropDownArrowDown from '../../../../assets/icons/dropdown-arrow-down.svg';
import useCardsAndBanks from '../../../../hooks/useCardsAndBanks';
import { getMMDDYY } from '../../../../helpers/utils/date';

import { useNavigate, useParams } from 'react-router-dom';
import useRolePermissions from '../../../../hooks/useRolePermissions';
import useCustomers from '../../../../hooks/useCustomers';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import ModalCardUsers from '../../../../components/Modals/ModalCardUsers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { setModalActions } from '../../../../store/slices/utils';
import { Alert } from '@piggytechng/component-library';
import { LuEye } from 'react-icons/lu';

const CardsAndBanksTab = () => {
  const { userCards, userBanks, cardUsers, isLoadingCardUsers, fetchCardUsers } = useCardsAndBanks();
  const { deleteUserCard, deleteUserBank, isRunningUserAction } = useCustomers();
  const [activeDropdown, setActiveDropdown] = useState('');
  const navigate = useNavigate();
  const { restrictActionByRole } = useRolePermissions({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<string | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [actionData, setActionData] = useState<any>({});
  const [showCardUsersModal, setShowCardUsersModal] = useState(false);
  const modalActions = useSelector((state: RootState) => state.modalActions);
  const dispatch = useDispatch();

  const params = useParams();
  const userIdIn = params.id as string;

  useEffect(() => {
    if (!activeDropdown) return;
    navigate(`?tab=cards-and-banks&active=${activeDropdown}`, { replace: true });
  }, [activeDropdown]);

  const handleAction = (action: string, cardId?: string) => {
    if (restrictActionByRole('bankAndCards', action)) return;
    if (action === 'deleteCard' && cardId) {
      setCardToDelete(cardId);
      setShowDeleteModal(true);
      return;
    }
    Alert.success('Action performed successfully');
  };

  const handleViewCardUsers = (cardId: string) => {
    setShowCardUsersModal(true);
    fetchCardUsers(cardId);
  };

  const allCards = userCards.map((card: any, index: any) => {
    const directDebitStatus = card?.type || 'Pending Verification';
    const isPendingVerification = directDebitStatus === 'Pending Verification';

    return (
      <div className="card-data" key={index}>
        <div className="details">
          <p className="provider-name">{card.provider}</p>
          <p className="card-name">{card.cardName}</p>
        </div>
        <div className="status-cell">
          <div
            className={`direct-debit-status ${
              isPendingVerification ? 'direct-debit-status--pending' : 'direct-debit-status--verified'
            }`}
          >
            <span className="direct-debit-status__dot" />
            {directDebitStatus}
          </div>
        </div>
        <div className="shared-users">
          <span className="shared-users__count">{card.sharedUsersCount ?? 0}</span>
          <span className="shared-users__label">
            {(card.sharedUsersCount ?? 0) === 1 ? 'Shared user' : 'Shared users'}
          </span>
        </div>
        <div className="dates">
          <span>Expires: {getMMDDYY(card.cardExpDate, 'MMM YYYY')}</span>
          <span>Added: {getMMDDYY(card.cardAddDate, 'MMM YYYY')}</span>
        </div>
        <div className="actions">
          <div className="icon" title="View card users" onClick={() => handleViewCardUsers(card.cardId)}>
            <LuEye size={20} color="#666766" />
          </div>
          <div className="icon" title="Delete card" onClick={() => handleAction('deleteCard', card.cardId)}>
            <img src={trashIcon} alt="" />
          </div>
        </div>
      </div>
    );
  });

  const withdrawalBanks = userBanks.map((bank: any, index: any) => {
    return (
      <div className="bank-data" key={index}>
        <div className="details">
          <p className="bank-name">{bank?.bankName}</p>
          <p className="account-number">{bank?.accountNumber}</p>
          <p className="user-name">{bank?.accountName}</p>
        </div>
        <div>
          <p className="added-date">Added: Jan 15, 2025</p>
        </div>
        <div className="flex-row items-center cursor-pointer">
          <span
            onClick={() => {
              setModalIsOpen(true);
              setActionData({
                bankName: bank.bankName,
                accountNumber: bank.accountNumber,
              });
            }}
          >
            <img src={trashIcon} alt="delete" />
          </span>
        </div>
      </div>
    );
  });

  return (
    <>
      <div className="cards-and-banks-tab">
        <div className="dropdown" onClick={() => setActiveDropdown(`${activeDropdown === 'card' ? '' : 'card'}`)}>
          <h1>Cards</h1>
          <span>
            <img src={activeDropdown === 'card' ? DropDownArrowDown : DropDownArrowUp} alt="" />
          </span>
        </div>
        <div className={`card-info ${activeDropdown !== 'card' && 'hide-dropdown'}`}>{allCards}</div>
        <div
          className="dropdown"
          onClick={() => setActiveDropdown(`${activeDropdown === 'withdrawals' ? '' : 'withdrawals'}`)}
        >
          <h1>Withdrawal Banks</h1>
          <span>
            <img src={activeDropdown === 'withdrawals' ? DropDownArrowDown : DropDownArrowUp} alt="" />
          </span>
        </div>
        <div className={`bank-info ${activeDropdown !== 'withdrawals' && 'hide-dropdown'}`}>{withdrawalBanks}</div>
      </div>
      <ModalExecuteAction
        modalIsOpen={showDeleteModal}
        setIsOpen={(state: boolean) => {
          setShowDeleteModal(state);
          if (!state) {
            dispatch(setModalActions({ ...modalActions, password: '' }));
          }
        }}
        action={(password: string) => {
          deleteUserCard(userIdIn, { cardId: cardToDelete, password }, () => setShowDeleteModal(false));
        }}
        text="delete this card?"
        actionColor="#FF4D4F"
        isLoading={isRunningUserAction}
      />

      <ModalExecuteAction
        modalIsOpen={modalIsOpen}
        setIsOpen={(state: boolean) => {
          setModalIsOpen(state);
          if (!state) {
            dispatch(setModalActions({ ...modalActions, password: '' }));
          }
        }}
        action={(password: string) => {
          deleteUserBank(userIdIn, { ...actionData, password }, () => setModalIsOpen(false));
        }}
        text="delete this withdrawal bank?"
        actionColor="#FF4D4F"
        isLoading={isRunningUserAction}
      />

      <ModalCardUsers
        modalIsOpen={showCardUsersModal}
        setIsOpen={setShowCardUsersModal}
        data={cardUsers}
        isLoading={isLoadingCardUsers}
      />
    </>
  );
};

export default CardsAndBanksTab;
